import { useCallback, useEffect, useRef } from 'react'

import {
  denormalizePoint,
  distanceToStroke,
  hexToRgba,
  type InkPoint,
  type InkStroke,
  type InkTool,
  normalizePointer,
} from './ink'
import { isShapeTool, type SurfaceMessage } from './types'

interface Props {
  /** When false, canvas is view-only (pointer-events none). */
  canDraw: boolean
  tool: InkTool
  /** Selected palette colour (hex). Highlighter reuses it at low alpha. */
  color: string
  strokes: InkStroke[]
  identity: string
  /** Active screen-share track SID — scopes ink to this share only. */
  shareId: string
  publishInk: (msg: SurfaceMessage, options?: { reliable?: boolean }) => void
  /** Remove a single stroke by id — the eraser tool. */
  eraseInk: (id: string) => void
  /**
   * Optional video element for object-fit:contain mapping. Falls back to the
   * host box when the video has not produced dimensions yet.
   */
  videoSelector?: string
}

const PEN_WIDTH = 3
const HIGHLIGHTER_WIDTH = 18
const SHAPE_WIDTH = 3
const HIGHLIGHTER_ALPHA = 0.4
/** A tap within this normalized distance of a stroke erases it. */
const ERASE_RADIUS = 0.02

/**
 * Minimum movement, in normalized units, before a point is recorded.
 *
 * A pointer emits events as fast as the screen refreshes — 120/second on newer
 * phones — and every one of those became a packet AND a point in the stroke. This
 * drops the ones that carry no information: at ~0.4% of the share's width the
 * line is visually identical and the traffic is a fraction.
 */
const MIN_POINT_DELTA = 0.004

/**
 * Hard ceiling on one freehand stroke.
 *
 * 'ink_end' carries the whole point list as the authoritative copy (so students
 * who missed lossy points still get the finished stroke). Unbounded, a long
 * highlighter sweep pushed that message past LiveKit's reliable cap and the
 * publish failed — the stroke then never finalized for anyone else. At this cap
 * the message stays comfortably inside the budget. (`publishInk` now retries a
 * failed end once, but a packet over the cap fails every time; staying under it
 * is still the actual fix.)
 */
const MAX_STROKE_POINTS = 300

/**
 * Transparent canvas over the screen share. Coordinates are normalized to the
 * video content box so different aspect ratios stay roughly aligned.
 *
 * The teacher gets a small kit: freehand pen and highlighter, straight line, arrow
 * (to point at a line of code), rectangle and ellipse (to box a region), an
 * eraser, and a colour. Shapes stream their end point as they are dragged, so
 * students see the box or arrow being drawn rather than only the final result.
 */
export function ShareInkLayer({
  canDraw,
  tool,
  color,
  strokes,
  identity,
  shareId,
  publishInk,
  eraseInk,
  videoSelector = '.stage__share-video',
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const hostRef = useRef<HTMLDivElement>(null)
  const drawing = useRef<{
    id: string
    points: InkPoint[]
    tool: InkTool
    color: string
    width: number
    /** Set once the cap is hit, so we stop sending but keep the stroke usable. */
    capped: boolean
  } | null>(null)
  /** Last committed canvas size, so we only reallocate when it really changes. */
  const sizeRef = useRef({ w: 0, h: 0, dpr: 0 })

  const videoMetrics = useCallback(() => {
    const host = hostRef.current
    if (!host) return { rect: new DOMRect(), vw: 0, vh: 0 }
    const rect = host.getBoundingClientRect()
    const video = host.parentElement?.querySelector(videoSelector) as HTMLVideoElement | null
    const vw = video?.videoWidth ?? 0
    const vh = video?.videoHeight ?? 0
    return { rect, vw, vh }
  }, [videoSelector])

  const paintRef = useRef<() => void>(() => {})
  // Paint all strokes whenever they change. The paint fn is exposed through a
  // ref so the ResizeObserver below (created once) can repaint on resize
  // without being rebuilt on every incoming ink packet — a teacher annotating
  // used to construct + disconnect a ResizeObserver per ink_point.
  useEffect(() => {
    const canvas = canvasRef.current
    const host = hostRef.current
    if (!canvas || !host) return

    const paint = () => {
      const dpr = window.devicePixelRatio || 1
      const { width, height } = host.getBoundingClientRect()
      if (width <= 0 || height <= 0) return
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      // Only resize when the box actually changed. Assigning canvas.width — even
      // the same value — throws away and reallocates the backing store, and this
      // paints on every incoming point, so doing it unconditionally meant a full
      // reallocation per point on the phones least able to afford one.
      const size = sizeRef.current
      if (size.w !== width || size.h !== height || size.dpr !== dpr) {
        canvas.width = Math.round(width * dpr)
        canvas.height = Math.round(height * dpr)
        canvas.style.width = `${width}px`
        canvas.style.height = `${height}px`
        sizeRef.current = { w: width, h: height, dpr }
      }
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.clearRect(0, 0, width, height)

      const video = host.parentElement?.querySelector(videoSelector) as HTMLVideoElement | null
      const vw = video?.videoWidth ?? 0
      const vh = video?.videoHeight ?? 0
      const map = (p: InkPoint) => denormalizePoint(p, width, height, vw, vh)

      for (const stroke of strokes) drawStroke(ctx, stroke, map)
    }

    paintRef.current = paint
    paint()
  }, [strokes, videoSelector])

  // One ResizeObserver for the layer's whole life, instead of one recreated
  // inside the paint effect on every strokes change. It forwards to the latest
  // paint fn through the ref, so a resize still repaints with the current
  // strokes and the host's current box.
  useEffect(() => {
    const host = hostRef.current
    if (!host) return
    const ro = new ResizeObserver(() => paintRef.current())
    ro.observe(host)
    return () => ro.disconnect()
  }, [])

  const styleFor = (t: InkTool) => {
    if (t === 'highlighter')
      return { color: hexToRgba(color, HIGHLIGHTER_ALPHA), width: HIGHLIGHTER_WIDTH }
    if (isShapeTool(t)) return { color, width: SHAPE_WIDTH }
    return { color, width: PEN_WIDTH }
  }

  const onPointerDown = (event: React.PointerEvent) => {
    if (!canDraw) return
    const { rect, vw, vh } = videoMetrics()
    const point = normalizePointer(event.clientX, event.clientY, rect, vw, vh)
    if (!point) return

    // Eraser is a tap, not a stroke: find the nearest stroke under the finger and
    // remove that one. Nothing is drawn or streamed.
    if (tool === 'eraser') {
      let best: { id: string; d: number } | null = null
      for (const s of strokes) {
        const d = distanceToStroke(s, point)
        if (d < ERASE_RADIUS && (!best || d < best.d)) best = { id: s.id, d }
      }
      if (best) eraseInk(best.id)
      return
    }

    const id = `ink-${identity}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const { color: strokeColor, width } = styleFor(tool)
    drawing.current = { id, points: [point], tool, color: strokeColor, width, capped: false }
    ;(event.target as HTMLElement).setPointerCapture?.(event.pointerId)
    // `at` carries the START point on the reliable channel, so the store has the
    // origin the moment the stroke exists. Without an origin the first *move*
    // becomes it — a shape rubber-bands from the wrong corner and a freehand line
    // misses its first point — until the reliable ink_end snaps it right on
    // release. Everyone paints from the store, so this is visible mid-draw to the
    // teacher and students both.
    publishInk({
      kind: 'ink_begin',
      id,
      shareId,
      tool,
      color: strokeColor,
      width,
      by: identity,
      at: point,
    })
    // The same origin, again, as a lossy point — KEPT ON PURPOSE for one release.
    // A live class can hold clients loaded before `at` shipped, and they ignore
    // an unknown field, so this is the only thing that still gives them an
    // origin. Harmless to a receiver that already seeded from `at`: coordinates
    // come from the same rounded `normalizePointer` call, so it is a duplicate of
    // a point that is already there. Delete this once old clients are gone.
    publishInk({ kind: 'ink_point', id, shareId, x: point.x, y: point.y }, { reliable: false })
  }

  const onPointerMove = (event: React.PointerEvent) => {
    const current = drawing.current
    if (!canDraw || !current) return
    if (current.capped) return
    const { rect, vw, vh } = videoMetrics()
    const point = normalizePointer(event.clientX, event.clientY, rect, vw, vh)
    if (!point) return

    if (isShapeTool(current.tool)) {
      // A shape is start + current end. Skip a move too small to see — same cheap
      // throttle freehand uses — then replace the end and stream it so the
      // rubber-band is visible to everyone. There is no path to accumulate.
      const end = current.points[1] ?? current.points[0]
      if (end && Math.hypot(point.x - end.x, point.y - end.y) < MIN_POINT_DELTA) return
      current.points = [current.points[0], point]
      publishInk(
        { kind: 'ink_point', id: current.id, shareId, x: point.x, y: point.y },
        { reliable: false },
      )
      return
    }

    // Freehand: ignore movement too small to see, then append. This is the single
    // cheapest thing we do for a low-end Android — it cuts packets and repaints.
    const previous = current.points[current.points.length - 1]
    if (previous) {
      if (Math.hypot(point.x - previous.x, point.y - previous.y) < MIN_POINT_DELTA) return
    }
    current.points.push(point)
    if (current.points.length >= MAX_STROKE_POINTS) current.capped = true
    publishInk(
      { kind: 'ink_point', id: current.id, shareId, x: point.x, y: point.y },
      { reliable: false },
    )
  }

  const endStroke = () => {
    const current = drawing.current
    if (!current) return
    drawing.current = null
    // A shape needs two distinct ends; a tap that never moved is nothing to
    // finalize. But ink_begin (and the seed point) already put a live entry in
    // the store, so it must be removed rather than just abandoned — otherwise a
    // stray tap leaves an invisible entry the eraser can later catch.
    if (isShapeTool(current.tool) && current.points.length < 2) {
      eraseInk(current.id)
      return
    }
    publishInk({
      kind: 'ink_end',
      id: current.id,
      shareId,
      points: current.points,
      tool: current.tool,
      color: current.color,
      width: current.width,
      by: identity,
    })
  }

  return (
    <div
      className={`ink-layer ${canDraw ? 'ink-layer--draw' : ''} ${canDraw ? `ink-layer--${tool}` : ''}`}
      ref={hostRef}
    >
      <canvas
        ref={canvasRef}
        className="ink-layer__canvas"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endStroke}
        onPointerCancel={endStroke}
      />
    </div>
  )
}

type Mapped = (p: InkPoint) => { x: number; y: number }

/** Render one stroke or shape onto the canvas in device pixels. */
function drawStroke(ctx: CanvasRenderingContext2D, stroke: InkStroke, map: Mapped): void {
  if (stroke.points.length < 1) return
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.strokeStyle = stroke.color
  ctx.fillStyle = stroke.color
  ctx.lineWidth = stroke.width

  const a = map(stroke.points[0])
  const b = map(stroke.points[stroke.points.length - 1])

  switch (stroke.tool) {
    case 'rect': {
      ctx.strokeRect(
        Math.min(a.x, b.x),
        Math.min(a.y, b.y),
        Math.abs(b.x - a.x),
        Math.abs(b.y - a.y),
      )
      return
    }
    case 'ellipse': {
      ctx.beginPath()
      ctx.ellipse(
        (a.x + b.x) / 2,
        (a.y + b.y) / 2,
        Math.abs(b.x - a.x) / 2,
        Math.abs(b.y - a.y) / 2,
        0,
        0,
        Math.PI * 2,
      )
      ctx.stroke()
      return
    }
    case 'line': {
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
      ctx.stroke()
      return
    }
    case 'arrow': {
      ctx.beginPath()
      ctx.moveTo(a.x, a.y)
      ctx.lineTo(b.x, b.y)
      ctx.stroke()
      // Arrowhead, scaled to the shaft so a tiny arrow does not sprout a huge head.
      const angle = Math.atan2(b.y - a.y, b.x - a.x)
      const head = Math.min(16, Math.hypot(b.x - a.x, b.y - a.y) * 0.35)
      const wing = Math.PI / 7
      ctx.beginPath()
      ctx.moveTo(b.x, b.y)
      ctx.lineTo(b.x - head * Math.cos(angle - wing), b.y - head * Math.sin(angle - wing))
      ctx.moveTo(b.x, b.y)
      ctx.lineTo(b.x - head * Math.cos(angle + wing), b.y - head * Math.sin(angle + wing))
      ctx.stroke()
      return
    }
    default: {
      // Freehand pen / highlighter.
      ctx.beginPath()
      const first = map(stroke.points[0])
      ctx.moveTo(first.x, first.y)
      for (let i = 1; i < stroke.points.length; i += 1) {
        const p = map(stroke.points[i])
        ctx.lineTo(p.x, p.y)
      }
      if (stroke.points.length === 1) ctx.lineTo(first.x + 0.01, first.y)
      ctx.stroke()
    }
  }
}
